import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

const faqs = [
  {
    q: "Do I need to keep the vehicle under my name?",
    a: "In many cases, the vehicle and loan remain under your name unless a formal transfer is completed. We guide you through available options and help manage the process.",
  },
  {
    q: "What vehicles qualify for your program?",
    a: "We typically work with vehicles from 2022 and newer. If your car is in good condition and still under financing, there's a strong chance we can help. Message us to quickly check your eligibility.",
  },
  {
    q: "How does your process work?",
    a: "We review your situation, then facilitate and manage the vehicle transfer process. We help match vehicle owners with qualified buyers and coordinate the next steps — without the hassle of going through a dealership.",
  },
  {
    q: "Can you help if I'm behind on payments?",
    a: "Yes. Many clients reach out when payments become difficult. We assess your situation and guide you through possible next steps as early as possible.",
  },
  {
    q: "Do you guarantee approval or transfer?",
    a: "No. We do not guarantee approvals or outcomes. Every situation is different. Our role is to guide and support you through the process.",
  },
  {
    q: "What does AutoManagement PH actually do?",
    a: "AutoManagement PH facilitates and manages a vehicle transition program. We help connect vehicle owners with qualified buyers and guide both sides through the process — making it simpler, faster, and more structured than handling it alone.",
  },
  {
    q: "Do I still need insurance?",
    a: "Once a new party takes over responsibility for the vehicle, they are required to secure and maintain active insurance coverage. This helps protect all parties involved during the transition.",
  },
  {
    q: "What if the next party fails to pay?",
    a: "AutoManagement PH provides structured support through a signed agreement. If the next party fails to meet their obligations, we step in and take action based on the agreed terms to protect the original vehicle owner.",
  },
  {
    q: "Is there any form of protection for me?",
    a: "Yes. Our process includes a formal agreement that outlines responsibilities and protections for the vehicle owner. This ensures there is a clear structure in place throughout the transition.",
  },
  {
    q: "Will this affect my credit?",
    a: "Credit impact depends on your current situation and the actions taken. We help you understand possible outcomes so you can make an informed decision.",
  },
  {
    q: "Are there any fees?",
    a: "Fees, if any, depend on the services required. We explain everything clearly before you decide to move forward.",
  },
  {
    q: "How long does the process take?",
    a: "Timelines vary depending on your situation. Some cases move quickly, while others may take longer.",
  },
  {
    q: "What documents do I need?",
    a: "Typically: a valid government ID, vehicle details (make, model, year), and your loan or financing information. Additional documents may be needed depending on your case.",
  },
  {
    q: "Is this legal in the Philippines?",
    a: "Yes. All steps are handled through proper agreements and compliant processes. We only guide clients through structured and lawful options.",
  },
];

function AccordionItem({
  faq,
  index,
  isOpen,
  onToggle,
}: {
  faq: { q: string; a: string };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (bodyRef.current) {
      setHeight(isOpen ? bodyRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer"
      style={{
        border: isOpen ? "1px solid #BFDBFE" : "1px solid #E8EEFF",
        backgroundColor: isOpen ? "#EFF6FF" : "#FFFFFF",
        boxShadow: isOpen
          ? "0 4px 20px rgba(79,124,255,0.10)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onClick={onToggle}
    >
      {/* Header */}
      <div className="flex w-full items-center justify-between gap-4 px-6 py-5 select-none">
        <div className="flex items-center gap-3 min-w-0">
          {/* Number badge */}
          <span
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-200"
            style={{
              backgroundColor: isOpen ? "#DBEAFE" : "#F1F5F9",
              color: isOpen ? "#2563EB" : "#94A3B8",
            }}
          >
            {index + 1}
          </span>
          <span
            className="text-sm font-semibold leading-6"
            style={{ color: isOpen ? "#1D4ED8" : "#1E293B" }}
          >
            {faq.q}
          </span>
        </div>

        {/* Chevron */}
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300"
          style={{
            backgroundColor: isOpen ? "#DBEAFE" : "#F1F5F9",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <i
            className="ri-arrow-down-s-line text-lg"
            style={{ color: isOpen ? "#2563EB" : "#64748B" }}
          />
        </span>
      </div>

      {/* Collapsible body */}
      <div
        ref={bodyRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: `${height}px`, opacity: isOpen ? 1 : 0 }}
      >
        <div
          className="mx-6 mb-5 rounded-xl px-5 py-4"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #DBEAFE",
          }}
        >
          <p
            className="text-sm leading-relaxed"
            style={{ color: "#475569", lineHeight: "1.85" }}
          >
            {faq.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="faq" className="py-24 lg:py-32" style={{ backgroundColor: "#F0F5FF" }}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.7fr] lg:items-start">

          {/* ── Left intro panel ── */}
          <div
            className="rounded-2xl p-8 lg:sticky lg:top-24"
            style={{
              backgroundColor: "#EBF0FF",
              border: "1px solid #DDEAFF",
            }}
          >
            <p
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: "#4F7CFF" }}
            >
              FAQ
            </p>
            <h2
              className="mt-3 text-3xl font-bold tracking-tight leading-tight sm:text-4xl"
              style={{ color: "#1E293B" }}
            >
              Frequently Asked Questions
            </h2>
            <p
              className="mt-4 text-sm leading-7"
              style={{ color: "#475569", lineHeight: "1.8" }}
            >
              Clear answers to help you understand your options.
            </p>

            <div
              className="mt-8 h-px w-12"
              style={{ backgroundColor: "#BFCFFF" }}
            />

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              {[
                { value: "14", label: "Topics covered" },
                { value: "100%", label: "Legal pathways" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl p-4 text-center"
                  style={{ backgroundColor: "#FFFFFF", border: "1px solid #DDEAFF" }}
                >
                  <div className="text-xl font-black" style={{ color: "#4F7CFF" }}>
                    {s.value}
                  </div>
                  <div className="mt-0.5 text-xs" style={{ color: "#64748B" }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-6 rounded-xl p-4 flex items-start gap-3"
              style={{ backgroundColor: "#FFFFFF", border: "1px solid #DDEAFF" }}
            >
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center mt-0.5">
                <i className="ri-question-answer-line text-sm" style={{ color: "#4F7CFF" }} />
              </div>
              <div>
                <p className="text-xs font-semibold" style={{ color: "#1E293B" }}>
                  Still have questions?
                </p>
                <Link
                  to="/intake"
                  className="mt-1 inline-flex items-center gap-1 text-xs font-semibold transition cursor-pointer whitespace-nowrap"
                  style={{ color: "#4F7CFF" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "#3D6AE8")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "#4F7CFF")
                  }
                >
                  Get a free review
                  <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>
          </div>

          {/* ── Right accordion ── */}
          <div className="flex flex-col" style={{ gap: "14px" }}>
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                faq={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => handleToggle(i)}
              />
            ))}

            {/* Disclaimer */}
            <div
              className="mt-1 rounded-xl px-5 py-4 flex items-center gap-3"
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E8EEFF",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            >
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                <i
                  className="ri-information-line text-sm"
                  style={{ color: "#7B8CFF" }}
                />
              </div>
              <p className="text-xs leading-5" style={{ color: "#94A3B8" }}>
                Options and outcomes vary based on each individual situation.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}